import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, Flame, Coffee, Clock, Zap, Sparkles, Image } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BMC_URL = "https://buymeacoffee.com/hypnoticvow";

// Set promotion end date (30 days from now)
const PROMO_END_DATE = new Date();
PROMO_END_DATE.setDate(PROMO_END_DATE.getDate() + 30);

const Pricing = () => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [shortsQuantity, setShortsQuantity] = useState('1');
  const [stillsQuantity, setStillsQuantity] = useState('1');

  // Pricing
  const SHORTS_PRICE = 0.50; // 50p per clip
  const STILLS_PRICE = 3; // £3 per video

  const shortsTotal = (parseInt(shortsQuantity) * SHORTS_PRICE).toFixed(2);
  const stillsTotal = (parseInt(stillsQuantity) * STILLS_PRICE).toFixed(2);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = PROMO_END_DATE - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleBuyNow = (amount) => {
    window.open(`${BMC_URL}?amount=${amount}`, '_blank');
  };

  const TimerDisplay = ({ small = false }) => (
    <div className={`flex items-center justify-center ${small ? 'gap-1' : 'gap-2'} text-center`}>
      <div className="bg-black/50 px-2 py-1 rounded">
        <span className={`font-syne font-bold ${small ? 'text-base' : 'text-xl'} text-neon-red`}>{timeLeft.days}</span>
        <p className="font-manrope text-[8px] text-neutral-400 uppercase">Days</p>
      </div>
      <span className="text-neon-red font-bold text-sm">:</span>
      <div className="bg-black/50 px-2 py-1 rounded">
        <span className={`font-syne font-bold ${small ? 'text-base' : 'text-xl'} text-neon-red`}>{String(timeLeft.hours).padStart(2, '0')}</span>
        <p className="font-manrope text-[8px] text-neutral-400 uppercase">Hrs</p>
      </div>
      <span className="text-neon-red font-bold text-sm">:</span>
      <div className="bg-black/50 px-2 py-1 rounded">
        <span className={`font-syne font-bold ${small ? 'text-base' : 'text-xl'} text-neon-red`}>{String(timeLeft.minutes).padStart(2, '0')}</span>
        <p className="font-manrope text-[8px] text-neutral-400 uppercase">Min</p>
      </div>
      <span className="text-neon-red font-bold text-sm">:</span>
      <div className="bg-black/50 px-2 py-1 rounded">
        <span className={`font-syne font-bold ${small ? 'text-base' : 'text-xl'} text-neon-red`}>{String(timeLeft.seconds).padStart(2, '0')}</span>
        <p className="font-manrope text-[8px] text-neutral-400 uppercase">Sec</p>
      </div>
    </div>
  );

  return (
    <section 
      id="pricing"
      data-testid="pricing-section"
      className="py-24 md:py-32 bg-void-black relative"
    >
      {/* Section Divider */}
      <div className="section-divider w-full absolute top-0" />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-16 md:mb-24"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          {/* Limited Time Banner */}
          <div className="inline-flex items-center gap-2 bg-neon-red/20 border border-neon-red/50 px-6 py-2 rounded-full mb-6">
            <Zap className="w-4 h-4 text-neon-red" />
            <span className="font-manrope text-sm font-bold text-neon-red uppercase tracking-wider">
              50% Off — Limited Time Only
            </span>
            <Zap className="w-4 h-4 text-neon-red" />
          </div>
          
          <h2 className="font-syne font-bold text-4xl md:text-5xl tracking-tight uppercase text-[#EDEDED]">
            Full Packages
          </h2>
          <p className="font-manrope text-neutral-400 mt-6 max-w-2xl mx-auto">
            Professional AI-generated videos at half price for the next month.
          </p>
        </motion.div>

        {/* Full Packages - Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {/* Quick Promo Card - £5 */}
          <motion.div
            data-testid="pricing-card-0"
            className="relative rounded-lg overflow-hidden"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0 }}
          >
            <div className="h-full p-8 rounded-lg glass">
              <div className="mb-4">
                <h3 className="font-syne font-bold text-xl text-[#EDEDED] uppercase">
                  Quick Promo
                </h3>
                <p className="font-manrope text-neutral-400 text-sm mt-1">
                  Perfect for social media teasers
                </p>
              </div>

              <div className="mb-4">
                <span className="font-syne text-2xl text-neutral-500 line-through mr-2">
                  £10
                </span>
                <span className="font-syne font-extrabold text-5xl text-[#EDEDED]">
                  £5
                </span>
                <span className="font-manrope text-neutral-400 text-sm ml-2">
                  per video
                </span>
              </div>

              {/* Timer */}
              <div className="mb-6 p-3 bg-black/30 rounded-lg">
                <p className="font-manrope text-[10px] text-neutral-400 uppercase mb-2 text-center">Offer ends in</p>
                <TimerDisplay />
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 flex-shrink-0 mt-0.5 text-neutral-400" />
                  <span className="font-manrope text-neutral-300 text-sm">Up to 2 minutes runtime</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 flex-shrink-0 mt-0.5 text-neutral-400" />
                  <span className="font-manrope text-neutral-300 text-sm">AI cinematic visuals</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 flex-shrink-0 mt-0.5 text-neutral-400" />
                  <span className="font-manrope text-neutral-300 text-sm">Social media ready</span>
                </li>
              </ul>

              <button
                data-testid="pricing-cta-0"
                onClick={() => handleBuyNow(5)}
                className="w-full py-4 rounded-none font-manrope font-bold text-sm tracking-widest uppercase border border-white/20 text-white hover:border-neon-red hover:text-neon-red transition-colors duration-300 flex items-center justify-center gap-2"
              >
                <Coffee className="w-4 h-4" />
                Buy Now
              </button>
            </div>
          </motion.div>

          {/* Full Movie Card - £10 */}
          <motion.div
            data-testid="pricing-card-1"
            className="relative rounded-lg overflow-hidden fire-card"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.15 }}
          >
            <div className="h-full p-8 rounded-lg bg-gradient-to-b from-neon-red/20 to-void-black border border-neon-red/50 neon-glow">
              <div className="absolute top-4 right-4 flex items-center gap-1 bg-neon-red px-3 py-1 rounded-full">
                <Flame className="w-4 h-4" />
                <span className="font-manrope text-xs font-bold uppercase">Popular</span>
              </div>

              <div className="mb-4">
                <h3 className="font-syne font-bold text-xl text-[#EDEDED] uppercase">
                  Full Movie
                </h3>
                <p className="font-manrope text-neutral-400 text-sm mt-1">
                  Complete AI movie experience
                </p>
              </div>

              <div className="mb-4">
                <span className="font-syne text-2xl text-neutral-500 line-through mr-2">
                  £20
                </span>
                <span className="font-syne font-extrabold text-5xl text-neon-red">
                  £10
                </span>
                <span className="font-manrope text-neutral-400 text-sm ml-2">
                  per video
                </span>
              </div>

              {/* Timer */}
              <div className="mb-6 p-3 bg-black/30 rounded-lg">
                <p className="font-manrope text-[10px] text-neutral-400 uppercase mb-2 text-center">Offer ends in</p>
                <TimerDisplay />
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 flex-shrink-0 mt-0.5 text-neon-red" />
                  <span className="font-manrope text-neutral-300 text-sm">Up to 3 minutes runtime</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 flex-shrink-0 mt-0.5 text-neon-red" />
                  <span className="font-manrope text-neutral-300 text-sm">Custom scenes & storyline</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 flex-shrink-0 mt-0.5 text-neon-red" />
                  <span className="font-manrope text-neutral-300 text-sm">Cinematic production</span>
                </li>
              </ul>

              <button
                data-testid="pricing-cta-1"
                onClick={() => handleBuyNow(10)}
                className="w-full py-4 rounded-none font-manrope font-bold text-sm tracking-widest uppercase bg-neon-red text-white hover:bg-red-600 neon-glow-hover transition-colors duration-300 flex items-center justify-center gap-2"
              >
                <Flame className="w-4 h-4" />
                <Coffee className="w-4 h-4" />
                Buy Now
              </button>
            </div>
          </motion.div>

          {/* Bulk Pack Card - £60 */}
          <motion.div
            data-testid="pricing-card-2"
            className="relative rounded-lg overflow-hidden"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <div className="h-full p-8 rounded-lg glass border border-white/10">
              {/* Best Value Badge */}
              <div className="absolute top-4 right-4 flex items-center gap-1 bg-white/10 px-3 py-1 rounded-full">
                <Clock className="w-4 h-4 text-white" />
                <span className="font-manrope text-xs font-bold uppercase text-white">Best Value</span>
              </div>

              <div className="mb-4">
                <h3 className="font-syne font-bold text-xl text-[#EDEDED] uppercase">
                  Bulk Pack
                </h3>
                <p className="font-manrope text-neutral-400 text-sm mt-1">
                  For serious content creators
                </p>
              </div>

              <div className="mb-4">
                <span className="font-syne text-2xl text-neutral-500 line-through mr-2">
                  £120
                </span>
                <span className="font-syne font-extrabold text-5xl text-[#EDEDED]">
                  £60
                </span>
                <span className="font-manrope text-neutral-400 text-sm ml-2">
                  5 videos
                </span>
              </div>

              {/* Timer */}
              <div className="mb-6 p-3 bg-black/30 rounded-lg">
                <p className="font-manrope text-[10px] text-neutral-400 uppercase mb-2 text-center">Offer ends in</p>
                <TimerDisplay />
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 flex-shrink-0 mt-0.5 text-neon-red" />
                  <span className="font-manrope text-neutral-300 text-sm font-medium">5 videos included</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 flex-shrink-0 mt-0.5 text-neon-red" />
                  <span className="font-manrope text-neutral-300 text-sm">1-5 minutes per video</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 flex-shrink-0 mt-0.5 text-neon-red" />
                  <span className="font-manrope text-neutral-300 text-sm">Choose your video lengths</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 flex-shrink-0 mt-0.5 text-neon-red" />
                  <span className="font-manrope text-neutral-300 text-sm">Dedicated support</span>
                </li>
                <li className="flex items-start gap-3">
                  <Zap className="w-5 h-5 flex-shrink-0 mt-0.5 text-neon-red" />
                  <span className="font-manrope text-neutral-300 text-sm font-medium">Fastest turnaround — within 1 week</span>
                </li>
              </ul>

              <button
                data-testid="pricing-cta-2"
                onClick={() => handleBuyNow(60)}
                className="w-full py-4 rounded-none font-manrope font-bold text-sm tracking-widest uppercase border border-white/20 text-white hover:border-neon-red hover:text-neon-red transition-colors duration-300 flex items-center justify-center gap-2"
              >
                <Coffee className="w-4 h-4" />
                Buy Now
              </button>
            </div>
          </motion.div>
        </div>

        {/* Budget Packages Section */}
        <motion.div 
          className="mt-24"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          {/* Budget Section Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-purple-500/20 border border-purple-500/50 px-6 py-2 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-purple-400" />
              <span className="font-manrope text-sm font-bold text-purple-400 uppercase tracking-wider">
                Budget Friendly — Limited Time
              </span>
              <Sparkles className="w-4 h-4 text-purple-400" />
            </div>
            <h3 className="font-syne font-bold text-3xl md:text-4xl tracking-tight uppercase text-[#EDEDED]">
              Starter Packages
            </h3>
            <p className="font-manrope text-neutral-400 mt-4 max-w-xl mx-auto">
              Perfect for creators on a budget. Choose how many you need!
            </p>
          </div>

          {/* Budget Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto">
            {/* Shorts Pack - 50p per clip */}
            <motion.div
              data-testid="pricing-card-shorts"
              className="relative rounded-lg overflow-hidden"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 }}
            >
              <div className="h-full p-8 rounded-lg glass border border-purple-500/30">
                <div className="absolute top-4 right-4 flex items-center gap-1 bg-purple-500/20 px-3 py-1 rounded-full">
                  <Zap className="w-4 h-4 text-purple-400" />
                  <span className="font-manrope text-xs font-bold uppercase text-purple-400">Super Cheap</span>
                </div>

                <div className="mb-4">
                  <h3 className="font-syne font-bold text-xl text-[#EDEDED] uppercase">
                    Shorts Clips
                  </h3>
                  <p className="font-manrope text-neutral-400 text-sm mt-1">
                    Perfect for TikTok, Reels & Shorts
                  </p>
                </div>

                <div className="mb-4">
                  <span className="font-syne text-2xl text-neutral-500 line-through mr-2">
                    £1
                  </span>
                  <span className="font-syne font-extrabold text-5xl text-purple-400">
                    50p
                  </span>
                  <span className="font-manrope text-neutral-400 text-sm ml-2">
                    per clip
                  </span>
                </div>

                {/* Timer */}
                <div className="mb-4 p-3 bg-black/30 rounded-lg">
                  <p className="font-manrope text-[10px] text-neutral-400 uppercase mb-2 text-center">Offer ends in</p>
                  <TimerDisplay small />
                </div>

                {/* Quantity Selector */}
                <div className="mb-4">
                  <label className="font-manrope text-xs text-neutral-400 uppercase mb-2 block">
                    How many clips?
                  </label>
                  <Select value={shortsQuantity} onValueChange={setShortsQuantity}>
                    <SelectTrigger 
                      data-testid="shorts-quantity-select"
                      className="w-full bg-black/50 border border-purple-500/30 rounded-none h-12 text-white font-manrope"
                    >
                      <SelectValue placeholder="Select quantity" />
                    </SelectTrigger>
                    <SelectContent className="bg-void-gray border border-purple-500/30">
                      <SelectItem value="1">1 clip — £0.50</SelectItem>
                      <SelectItem value="2">2 clips — £1.00</SelectItem>
                      <SelectItem value="3">3 clips — £1.50</SelectItem>
                      <SelectItem value="4">4 clips — £2.00</SelectItem>
                      <SelectItem value="5">5 clips — £2.50</SelectItem>
                      <SelectItem value="10">10 clips — £5.00</SelectItem>
                      <SelectItem value="20">20 clips — £10.00</SelectItem>
                      <SelectItem value="50">50 clips — £25.00</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Total */}
                <div className="mb-6 p-4 bg-purple-500/10 rounded-lg border border-purple-500/30">
                  <div className="flex justify-between items-center">
                    <span className="font-manrope text-sm text-neutral-300">Total:</span>
                    <span className="font-syne font-bold text-2xl text-purple-400">£{shortsTotal}</span>
                  </div>
                  <p className="font-manrope text-xs text-neutral-500 mt-1">
                    {shortsQuantity} × 6 second clips
                  </p>
                </div>

                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 flex-shrink-0 mt-0.5 text-purple-400" />
                    <span className="font-manrope text-neutral-300 text-sm">6 seconds per clip</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 flex-shrink-0 mt-0.5 text-purple-400" />
                    <span className="font-manrope text-neutral-300 text-sm">AI generated visuals</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 flex-shrink-0 mt-0.5 text-purple-400" />
                    <span className="font-manrope text-neutral-300 text-sm">Perfect for vertical content</span>
                  </li>
                </ul>

                <button
                  data-testid="pricing-cta-shorts"
                  onClick={() => handleBuyNow(parseFloat(shortsTotal))}
                  className="w-full py-4 rounded-none font-manrope font-bold text-sm tracking-widest uppercase border border-purple-500/30 text-white hover:border-purple-400 hover:text-purple-400 transition-colors duration-300 flex items-center justify-center gap-2"
                >
                  <Coffee className="w-4 h-4" />
                  Buy {shortsQuantity} Clip{shortsQuantity !== '1' ? 's' : ''} — £{shortsTotal}
                </button>
              </div>
            </motion.div>

            {/* Still Image Music Video - £3 per video */}
            <motion.div
              data-testid="pricing-card-stills"
              className="relative rounded-lg overflow-hidden"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="h-full p-8 rounded-lg glass border border-purple-500/30">
                <div className="absolute top-4 right-4 flex items-center gap-1 bg-purple-500/20 px-3 py-1 rounded-full">
                  <Image className="w-4 h-4 text-purple-400" />
                  <span className="font-manrope text-xs font-bold uppercase text-purple-400">Budget Video</span>
                </div>

                <div className="mb-4">
                  <h3 className="font-syne font-bold text-xl text-[#EDEDED] uppercase">
                    Still Image Video
                  </h3>
                  <p className="font-manrope text-neutral-400 text-sm mt-1">
                    AI images with special effects
                  </p>
                </div>

                <div className="mb-4">
                  <span className="font-syne text-2xl text-neutral-500 line-through mr-2">
                    £6
                  </span>
                  <span className="font-syne font-extrabold text-5xl text-purple-400">
                    £3
                  </span>
                  <span className="font-manrope text-neutral-400 text-sm ml-2">
                    per video
                  </span>
                </div>

                {/* Timer */}
                <div className="mb-4 p-3 bg-black/30 rounded-lg">
                  <p className="font-manrope text-[10px] text-neutral-400 uppercase mb-2 text-center">Offer ends in</p>
                  <TimerDisplay small />
                </div>

                {/* Quantity Selector */}
                <div className="mb-4">
                  <label className="font-manrope text-xs text-neutral-400 uppercase mb-2 block">
                    How many videos?
                  </label>
                  <Select value={stillsQuantity} onValueChange={setStillsQuantity}>
                    <SelectTrigger 
                      data-testid="stills-quantity-select"
                      className="w-full bg-black/50 border border-purple-500/30 rounded-none h-12 text-white font-manrope"
                    >
                      <SelectValue placeholder="Select quantity" />
                    </SelectTrigger>
                    <SelectContent className="bg-void-gray border border-purple-500/30">
                      <SelectItem value="1">1 video — £3</SelectItem>
                      <SelectItem value="2">2 videos — £6</SelectItem>
                      <SelectItem value="3">3 videos — £9</SelectItem>
                      <SelectItem value="4">4 videos — £12</SelectItem>
                      <SelectItem value="5">5 videos — £15</SelectItem>
                      <SelectItem value="10">10 videos — £30</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Total */}
                <div className="mb-6 p-4 bg-purple-500/10 rounded-lg border border-purple-500/30">
                  <div className="flex justify-between items-center">
                    <span className="font-manrope text-sm text-neutral-300">Total:</span>
                    <span className="font-syne font-bold text-2xl text-purple-400">£{stillsTotal}</span>
                  </div>
                  <p className="font-manrope text-xs text-neutral-500 mt-1">
                    {stillsQuantity} × still image video{stillsQuantity !== '1' ? 's' : ''}
                  </p>
                </div>

                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 flex-shrink-0 mt-0.5 text-purple-400" />
                    <span className="font-manrope text-neutral-300 text-sm">Up to 3 minutes per video</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 flex-shrink-0 mt-0.5 text-purple-400" />
                    <span className="font-manrope text-neutral-300 text-sm">AI generated still images</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 flex-shrink-0 mt-0.5 text-purple-400" />
                    <span className="font-manrope text-neutral-300 text-sm">Special effects & transitions</span>
                  </li>
                </ul>

                <button
                  data-testid="pricing-cta-stills"
                  onClick={() => handleBuyNow(parseFloat(stillsTotal))}
                  className="w-full py-4 rounded-none font-manrope font-bold text-sm tracking-widest uppercase border border-purple-500/30 text-white hover:border-purple-400 hover:text-purple-400 transition-colors duration-300 flex items-center justify-center gap-2"
                >
                  <Coffee className="w-4 h-4" />
                  Buy {stillsQuantity} Video{stillsQuantity !== '1' ? 's' : ''} — £{stillsTotal}
                </button>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Additional Info */}
        <motion.div 
          className="text-center mt-16"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <p className="font-manrope text-neutral-500 text-sm">
            Secure payments via Buy Me a Coffee. Card, Apple Pay, Google Pay & PayPal accepted.
          </p>
          <a 
            href={BMC_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-4 text-neon-red hover:text-white transition-colors duration-300"
          >
            <Coffee className="w-5 h-5" />
            <span className="font-manrope text-sm font-medium">Support ShadowCell Studios</span>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Pricing;
